/* THE EXAMPLE DATA LAYER'S PostgREST — moved VERBATIM out of `dev/stub.ts` (2026-09-28, the example workspace), so the
 * dev render harness and the example workspace answer the app's real queries with ONE emulator, never two copies that
 * drift. It knows nothing about sessions, the stub's marker or the network: given a fixture and a request URL, it
 * answers as PostgREST would. Which requests REACH it is decided elsewhere (`dev/stub.ts` for the harness,
 * `lib/example/mode.ts` for the example workspace, which refuses every write before this is consulted).
 */

import { applyWeekTrees } from "./weekTrees";
import { applyAssign, applySaveWeekAsProgram } from "./weekLibrary";
import type { TreePayload } from "../weekEdit";

export type Rows = Record<string, unknown>[];
export type Fixture = { coachId: string; tables: Record<string, Rows> };

/* ── A very small PostgREST ───────────────────────────────────────────────────────────────────
 *
 * Enough of it to answer every query this app makes, and NOTHING beyond that. Each piece below
 * exists because a real call site needs it; a filter nobody uses is a filter nobody has tested.
 *
 * ⚠️ WHERE IT CANNOT ANSWER, IT SAYS SO IN THE CONSOLE AND RETURNS AN EMPTY LIST. An unknown
 * table quietly answering `[]` would be the exact failure this repo keeps paying for — a screen
 * that renders a real empty state for a reason that is not real.
 */

/** Parent table → the child tables that may be embedded under it, and the column that joins. */
const CHILD_LINKS: Record<string, Record<string, string>> = {
  workout_sessions: { workout_session_exercises: "workout_session_id" },
  workout_session_exercises: {
    set_logs: "workout_session_exercise_id",
    form_clips: "workout_session_exercise_id",
  },
  coach_workouts: { coach_workout_sections: "workout_id" },
  coach_workout_sections: { coach_workout_exercises: "section_id" },
  coach_workout_exercises: { coach_workout_sets: "workout_exercise_id" },
};

/** `coach_workouts!workout_id(name)` — a to-ONE embed reached through the PARENT's own column. */
const PARENT_LINKS: Record<string, Record<string, string>> = {
  coach_assignments: { coach_workouts: "workout_id" },
  coach_workout_exercises: { coach_exercises: "exercise_id" },
  // W112 — a logged set's session's scheduled day (`rosterData.SET_DAY_SELECT`): set → its exercise row → its session.
  set_logs: { workout_session_exercises: "workout_session_exercise_id" },
  workout_session_exercises: { workout_sessions: "workout_session_id" },
};

type Field = { name: string; embed: Field[] | null };

/** Split `a, b(c, d(e)), f` at TOP-LEVEL commas only. */
function splitFields(select: string): string[] {
  const out: string[] = [];
  let depth = 0;
  let current = "";
  for (const ch of select) {
    if (ch === "(") depth += 1;
    if (ch === ")") depth -= 1;
    if (ch === "," && depth === 0) { out.push(current); current = ""; continue; }
    current += ch;
  }
  if (current.trim() !== "") out.push(current);
  return out.map((f) => f.trim()).filter((f) => f !== "");
}

function parseSelect(select: string): Field[] {
  return splitFields(select).map((raw) => {
    const open = raw.indexOf("(");
    if (open === -1) return { name: raw, embed: null };
    const inner = raw.slice(open + 1, raw.lastIndexOf(")"));
    return { name: raw.slice(0, open).trim(), embed: parseSelect(inner) };
  });
}

function project(row: Record<string, unknown>, fields: Field[], table: string, fx: Fixture): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const field of fields) {
    // `coach_workouts!workout_id(name)` — the hint after `!` names the joining column.
    const parts = field.name.split("!");
    const bare = parts[0] ?? field.name;
    const hint = parts[1];
    if (field.embed === null) {
      if (bare === "*") { Object.assign(out, row); continue; }
      out[bare] = row[bare] ?? null;
      continue;
    }
    const childCol = CHILD_LINKS[table]?.[bare];
    if (childCol !== undefined) {
      const kids = (fx.tables[bare] ?? []).filter((k: Record<string, unknown>) => k[childCol] === row["id"]);
      if (fx.tables[bare] === undefined) warnMissing(bare);
      out[bare] = kids.map((k: Record<string, unknown>) => project(k, field.embed ?? [], bare, fx));
      continue;
    }
    const ownCol = hint ?? PARENT_LINKS[table]?.[bare];
    if (ownCol !== undefined) {
      const parent = (fx.tables[bare] ?? []).find((p: Record<string, unknown>) => p["id"] === row[ownCol]);
      if (fx.tables[bare] === undefined) warnMissing(bare);
      out[bare] = parent === undefined ? null : project(parent, field.embed, bare, fx);
      continue;
    }
    warnUnlinked(table, bare);
    out[bare] = [];
  }
  return out;
}

const warned = new Set<string>();
function warnMissing(table: string): void {
  if (warned.has(`m:${table}`)) return;
  warned.add(`m:${table}`);
  console.warn(`[emulator] table "${table}" is not in the fixture — it renders EMPTY, and that is the fixture, not the app. Declare it in the fixture (the dev dump or the example world).`);
}
function warnUnlinked(parent: string, child: string): void {
  if (warned.has(`l:${parent}.${child}`)) return;
  warned.add(`l:${parent}.${child}`);
  console.warn(`[emulator] no declared relationship ${parent} → ${child}; the embed renders EMPTY. Add it to CHILD_LINKS or PARENT_LINKS in src/lib/example/rest.ts.`);
}

/** PostgREST's `eq.x`, `in.(a,b)`, `is.null`, `gte.x` … applied to one row. */
function matches(row: Record<string, unknown>, op: string, raw: string): boolean {
  const value = row[op];
  const dot = raw.indexOf(".");
  const verb = raw.slice(0, dot);
  const arg = raw.slice(dot + 1);
  switch (verb) {
    case "eq":  return String(value) === arg;
    case "neq": return String(value) !== arg;
    case "gt":  return String(value) > arg;
    case "gte": return String(value) >= arg;
    case "lt":  return String(value) < arg;
    case "lte": return String(value) <= arg;
    case "is":  return arg === "null" ? value === null || value === undefined : String(value) === arg;
    case "not": return !matches(row, op, arg);
    case "in": {
      const set = arg.replace(/^\(|\)$/g, "").split(",").map((v) => v.replace(/^"|"$/g, ""));
      return set.includes(String(value));
    }
    default:
      warnUnlinked("filter", verb);
      return true;
  }
}

/* The RPCs this app calls, built from the SAME fixture rows the tables are served from — so the
 * tree a screen renders is his real workout, not an invention. Anything not listed answers `null`
 * and says so, which is a loud absence rather than a plausible empty screen. */
/** A save stamp that is new on every write, even two in one millisecond (the editor's token must move). */
let lastStamp = 0;
function nextStamp(): string {
  lastStamp = Math.max(Date.now(), lastStamp + 1);
  return new Date(lastStamp).toISOString();
}

export function answerRpc(url: URL, headers: Headers, fx: Fixture, init?: RequestInit): Response {
  const fn = url.pathname.replace(/^\/rest\/v1\/rpc\//, "");
  let args: Record<string, unknown> = {};
  try { args = JSON.parse(String(init?.body ?? "{}")) as Record<string, unknown>; } catch { args = {}; }

  /* Week editor stage 1 — the editor's one write, applied to THIS fixture in memory (the example's made-up world, or the dev
     stub's dump): nothing is sent anywhere. `mode.ts` lets exactly this RPC through to here while the example is on. */
  if (fn === "write_week_trees") {
    const r = applyWeekTrees(fx, (args["p_trees"] ?? []) as TreePayload[], nextStamp);
    if ("error" in r) {
      return new Response(JSON.stringify({ code: "P0001", message: r.error, details: null, hint: null }), {
        status: 400, headers: { "content-type": "application/json" },
      });
    }
    return new Response(JSON.stringify(r.updated), { status: 200, headers: { "content-type": "application/json" } });
  }

  /* Week editor stage 2 — a library workout assigned to a day, and a week saved as a program, in this fixture's memory. */
  if (fn === "assign_coach_workout" || fn === "save_week_as_program") {
    const r = fn === "assign_coach_workout" ? applyAssign(fx, args, nextStamp()) : applySaveWeekAsProgram(fx, args, nextStamp());
    if ("error" in r) {
      return new Response(JSON.stringify({ code: r.error.includes("unique") ? "23505" : "P0001", message: r.error, details: null, hint: null }), {
        status: 400, headers: { "content-type": "application/json" },
      });
    }
    return new Response(JSON.stringify(r.data), { status: 200, headers: { "content-type": "application/json" } });
  }

  if (fn === "assigned_workout_tree") {
    const workoutId = String(args["p_workout_id"] ?? "");
    const workout = (fx.tables["coach_workouts"] ?? []).find((w) => w["id"] === workoutId);
    if (workout === undefined) return json([], headers);
    const sections = (fx.tables["coach_workout_sections"] ?? [])
      .filter((s) => s["workout_id"] === workoutId);
    const tree = {
      ...workout,
      coach_workout_sections: sections.map((sec) => {
        const exercises = (fx.tables["coach_workout_exercises"] ?? [])
          .filter((e) => e["section_id"] === sec["id"]);
        return {
          ...sec,
          coach_workout_exercises: exercises.map((ex) => ({
            ...ex,
            // ⚠️ THE JOIN COLUMN IS `exercise_id`, AND IT WAS `coach_exercise_id` HERE — a name
            // NO ROW CARRIES, so this embed resolved to `null` for EVERY exercise in EVERY tree
            // and the stub silently served a tree with no exercise names in it. MEASURED against
            // the fixture: 577 of 577 `coach_workout_exercises` rows carry `exercise_id`, ZERO
            // carry `coach_exercise_id`, and `assignedWorkoutTree.ts` types the column
            // `exercise_id: string | null`. The visible symptom was one page over and looked like
            // somebody else's bug: the next-week rows rendered raw UUIDs, which read as INK's
            // brand-new "names in the next-week rows" having failed. It had not — the STAND-IN
            // could not express the state, which is this repo's own trap about stand-ins.
            coach_exercises: (fx.tables["coach_exercises"] ?? [])
              .find((c) => c["id"] === ex["exercise_id"]) ?? null,
            coach_workout_sets: (fx.tables["coach_workout_sets"] ?? [])
              .filter((st) => st["workout_exercise_id"] === ex["id"]),
          })),
        };
      }),
    };
    // An RPC returning a single composite comes back as the VALUE, not as a one-row array.
    return new Response(JSON.stringify(tree), {
      status: 200, headers: { "content-type": "application/json" },
    });
  }

  /* The invitation page's peek (2026-09-22 night): ONE token answers, as the fixture's own coach,
     so the page can be rendered in its real "found" state without writing a live invitation row.
     Every other token answers zero rows — the database's refusal shape for used/expired/invalid. */
  if (fn === "peek_coach_invitation") {
    const coach = (fx.tables["profiles"] ?? []).find((p) => p["id"] === fx.coachId);
    const name = [coach?.["first_name"], coach?.["last_name"]].filter(Boolean).join(" ");
    const rows = args["p_token"] === "stubinvitation0000000000"
      ? [{ coach_name: name, expires_at: "2026-10-22T00:00:00Z", invited_first_name: null, invited_last_name: null }]
      : [];
    return json(rows, headers);
  }

  // The reviewer seat (2026-10-10): the example coach reviews nobody — the live function's answer for such an account.
  if (fn === "reviewer_links") return json([], headers);

  warnMissing(`rpc/${fn}`);
  return new Response("null", { status: 200, headers: { "content-type": "application/json" } });
}

export function answer(url: URL, headers: Headers, fx: Fixture): Response {
  const table = url.pathname.replace(/^\/rest\/v1\//, "");
  const rows = fx.tables[table];
  /* W72 step 6 → 2026-09-26: the reactions table is LIVE (his "go on all"), so the stub answers as the live database now
     does — an empty list where no fixture seeds one, which the Inbox's probe reads as "the table exists". Until that
     day this answered PGRST205, exactly as the live database did. */
  if (rows === undefined && table === "coach_message_reactions") return json([], headers, 0);
  /* W82 step 6 → 2026-09-26: `coach_reminders` is LIVE (his rule (5)) — an empty list where no fixture seeds one, as the live
     database answers a coach who wrote none. `?ht-reminders=N` seeds N below. */
  if (rows === undefined && table === "coach_reminders") return json([], headers, 0);
  /* 2026-10-12: `coach_calls` is LIVE (scheduled calls, session 1) — an empty list where no fixture seeds one; `?ht-calls` seeds two. */
  if (rows === undefined && table === "coach_calls") return json([], headers, 0);
  if (rows === undefined) {
    warnMissing(table);
    return json([], headers, 0);
  }

  let out = rows.slice();
  for (const [key, raw] of url.searchParams.entries()) {
    if (["select", "order", "limit", "offset"].includes(key)) continue;
    /* 🔴 `or=(a.eq.X,b.eq.Y)` — PostgREST's disjunction, and it was silently DROPPED here until
       2026-09-19 (it fell to the `default` arm of `matches`, which returns true, so an `.or()`
       read every row in the table). W17d's Delete asks exactly this question — "does any
       assignment name this workout, through either column" — so a stub that answers "all of
       them" would have rendered a confirm sentence about a template nothing points at. */
    if (key === "or") {
      const clauses = raw.replace(/^\(|\)$/g, "").split(",");
      out = out.filter((row) => clauses.some((c) => {
        const dot = c.indexOf(".");
        return matches(row, c.slice(0, dot), c.slice(dot + 1));
      }));
      continue;
    }
    out = out.filter((row) => matches(row, key, raw));
  }
  // The count is taken BEFORE `limit`, exactly as PostgREST does — a count that shrinks with the
  // page is not a count.
  const total = out.length;

  const order = url.searchParams.get("order");
  if (order !== null) {
    const col = order.split(".")[0] ?? "id";
    const sign = order.endsWith(".desc") ? -1 : 1;
    out.sort((a, b) => (String(a[col] ?? "") < String(b[col] ?? "") ? -sign : String(a[col] ?? "") > String(b[col] ?? "") ? sign : 0));
  }

  const limit = url.searchParams.get("limit");
  if (limit !== null) out = out.slice(0, Number(limit));

  const select = url.searchParams.get("select") ?? "*";
  const shaped = out.map((row) => project(row, parseSelect(select), table, fx));
  return json(shaped, headers, total);
}

/** `maybeSingle()`/`single()` ask for an OBJECT through the Accept header, not through the URL. */
export function json(rows: Rows, headers: Headers, total?: number): Response {
  const wantsObject = (headers.get("accept") ?? "").includes("vnd.pgrst.object");
  const body = wantsObject ? (rows[0] ?? null) : rows;
  const status = wantsObject && rows.length === 0 ? 406 : 200;
  const out: Record<string, string> = { "content-type": "application/json" };
  /* 🔴 THE COUNT TRAVELS IN A HEADER, NOT IN THE BODY, and this stub did not send it until
     2026-09-19 — so `select("id", { count: "exact", head: true })` came back with `count: null`
     on every screen that asked. `null` is not zero, and the one caller that reads a count here
     (W17d's Delete) correctly REFUSES on a null rather than guessing — which rendered as a
     failure on the harness while the same code is right against the real database. An
     instrument that cannot express the state answers a different question, confidently. */
  if ((headers.get("prefer") ?? "").includes("count=") && total !== undefined) {
    out["content-range"] = rows.length === 0
      ? `*/${String(total)}`
      : `0-${String(rows.length - 1)}/${String(total)}`;
  }
  return new Response(JSON.stringify(body), { status, headers: out });
}

